import { Injectable } from '@angular/core';
import { Observable, fromEvent, map, startWith, distinctUntilChanged, shareReplay, debounceTime } from 'rxjs';

/**
 * Enum representing Bootstrap's breakpoints
 * - XS: Extra small (<576px)
 * - SM: Small (≥576px)
 * - MD: Medium (≥768px)
 * - LG: Large (≥992px)
 * - XL: Extra large (≥1200px)
 * - XXL: Extra extra large (≥1400px)
 */
export enum BreakPoint {
  XS = 'xs',
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
  XL = 'xl',
  XXL = 'xxl'
}

/**
 * Service for detecting Bootstrap breakpoints
 */
@Injectable({
  providedIn: 'root'
})
export class BreakpointService {
  // Bootstrap breakpoints in pixels
  private readonly breakpoints = {
    [BreakPoint.XS]: 0,
    [BreakPoint.SM]: 576,
    [BreakPoint.MD]: 768,
    [BreakPoint.LG]: 992,
    [BreakPoint.XL]: 1200,
    [BreakPoint.XXL]: 1400
  };

  // Observable that emits the current breakpoint whenever window size changes
  private breakpoint$: Observable<BreakPoint> = fromEvent(window, 'resize').pipe(
    debounceTime(100),
    startWith(window.innerWidth), // Start with the actual width instead of null
    map(() => this.getCurrentBreakpoint()),
    distinctUntilChanged(),
    shareReplay(1)
  );

  /**
   * Gets the current Bootstrap breakpoint
   * @returns The current breakpoint as BreakPoint enum
   */
  getCurrentBreakpoint(): BreakPoint {
    const width = window.innerWidth;

    if (width < this.breakpoints[BreakPoint.SM]) {
      return BreakPoint.XS;
    } else if (width < this.breakpoints[BreakPoint.MD]) {
      return BreakPoint.SM;
    } else if (width < this.breakpoints[BreakPoint.LG]) {
      return BreakPoint.MD;
    } else if (width < this.breakpoints[BreakPoint.XL]) {
      return BreakPoint.LG;
    } else if (width < this.breakpoints[BreakPoint.XXL]) {
      return BreakPoint.XL;
    } else {
      return BreakPoint.XXL;
    }
  }

  /**
   * Checks if the current breakpoint is at or above the specified breakpoint
   * @param breakpoint The breakpoint to check against
   * @returns True if current width is at or above the specified breakpoint
   */
  isBreakpointOrLarger(breakpoint: BreakPoint): boolean {
    const width = window.innerWidth;
    return width >= this.breakpoints[breakpoint];
  }

  /**
   * Checks if the current breakpoint is below the specified breakpoint
   * @param breakpoint The breakpoint to check against
   * @returns True if current width is below the specified breakpoint
   */
  isBreakpointSmaller(breakpoint: BreakPoint): boolean {
    const width = window.innerWidth;
    return width < this.breakpoints[breakpoint];
  }

  /**
   * Observes changes to the current breakpoint
   * @returns Observable that emits the current breakpoint when it changes
   */
  observe(): Observable<BreakPoint> {
    return this.breakpoint$;
  }

  /**
   * Observes if the breakpoint is at or above the specified breakpoint
   * @param breakpoint The breakpoint to check against
   * @returns Observable that emits true when width is at or above the specified breakpoint
   */
  observeIsBreakpointOrLarger(breakpoint: BreakPoint): Observable<boolean> {
    return this.observe().pipe(
      map(currentBreakpoint => {
        const currentIndex = Object.values(BreakPoint).indexOf(currentBreakpoint);
        const targetIndex = Object.values(BreakPoint).indexOf(breakpoint);
        return currentIndex >= targetIndex;
      })
    );
  }

  /**
   * Observes if the breakpoint matches or is smaller than the specified breakpoint
   * @param breakpoint The breakpoint to check against
   * @returns Observable that emits true when width matches or is smaller than the specified breakpoint
   */
  observeIsBreakpointOrSmaller(breakpoint: BreakPoint): Observable<boolean> {
    return this.observe().pipe(
      map(currentBreakpoint => {
        const currentIndex = Object.values(BreakPoint).indexOf(currentBreakpoint);
        const targetIndex = Object.values(BreakPoint).indexOf(breakpoint);
        return currentIndex <= targetIndex;
      })
    );
  }
}
